import { useLayoutEffect, useRef, type CSSProperties, type ReactNode } from "react";
import { cn } from "@/lib/utils";

/****************
 * ONE LINE. Guillaume's layout rule, 2026-10-21: "labels and values that
 * belong together never wrap; scale the text down instead."
 *
 * Renders its children on a single line. When they do not fit the width the
 * parent gives, the font is scaled down (to `min`, 60% by default) until they
 * do; past that the end is cut with an ellipsis rather than wrapping. Re-fits
 * whenever the box or the text changes size.
 *
 * Same rule on the phone: mobile-app/src/components/primitives/OneLine.tsx.
 ****************/
export function OneLine({
  children,
  className,
  style,
  min = 0.6,
  as: Tag = "span",
  title,
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  /** Smallest scale allowed, as a share of the inherited size. */
  min?: number;
  as?: "span" | "div" | "p" | "h1" | "h2" | "h3";
  title?: string;
}) {
  const ref = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    let frame = 0;
    // Keep a size set inline by the caller; only our own shrink is undone.
    const original = el.style.fontSize;
    const base = parseFloat(original || getComputedStyle(el).fontSize) || 14;
    const fit = () => {
      el.style.fontSize = original;
      let scale = 1;
      // Shrink in 5% steps; at most 8 passes from 100% to 60%.
      while (el.scrollWidth > el.clientWidth + 0.5 && scale > min) {
        scale = Math.max(min, scale - 0.05);
        el.style.fontSize = `${base * scale}px`;
      }
    };
    fit();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(fit);
    });
    ro.observe(el);
    if (el.parentElement) ro.observe(el.parentElement);
    return () => {
      cancelAnimationFrame(frame);
      ro.disconnect();
    };
  }, [children, min]);

  return (
    <Tag
      ref={ref as never}
      title={title}
      className={cn("block min-w-0 max-w-full overflow-hidden text-ellipsis whitespace-nowrap", className)}
      style={style}
    >
      {children}
    </Tag>
  );
}

/****************
 * THE SAME RULE FOR EVERY "truncate" ON THE PAGE. 2026-10-21, layout sweep.
 *
 * Tailwind's `truncate` cuts a line with "..." when it does not fit, and the
 * app uses it in 80+ places (tables, cards, trays). Guillaume's rule is to
 * shrink before cutting. Mounted once in the layout: every `.truncate` element,
 * present or added later, is shrunk in 5% steps to 60% before the ellipsis.
 ****************/
function fitElement(el: HTMLElement, min: number) {
  const original = el.dataset.fitBase ?? el.style.fontSize;
  el.dataset.fitBase = original;
  el.style.fontSize = original;
  if (el.scrollWidth <= el.clientWidth + 0.5) return;
  const base = parseFloat(getComputedStyle(el).fontSize) || 14;
  let scale = 1;
  while (el.scrollWidth > el.clientWidth + 0.5 && scale > min) {
    scale = Math.max(min, scale - 0.05);
    el.style.fontSize = `${base * scale}px`;
  }
}

export function useFitAllTruncated(min = 0.6) {
  useLayoutEffect(() => {
    if (typeof ResizeObserver === "undefined" || typeof MutationObserver === "undefined") return;
    const watched = new WeakSet<Element>();
    let frame = 0;
    const pending = new Set<HTMLElement>();
    const flush = () => { frame = 0; pending.forEach((el) => el.isConnected && fitElement(el, min)); pending.clear(); };
    const queue = (el: HTMLElement) => { pending.add(el); if (!frame) frame = requestAnimationFrame(flush); };
    const ro = new ResizeObserver((entries) => entries.forEach((e) => queue(e.target as HTMLElement)));
    const scan = (root: ParentNode) => (root.querySelectorAll(".truncate") as NodeListOf<HTMLElement>).forEach((el) => {
      if (!watched.has(el)) { watched.add(el); ro.observe(el); }
      queue(el);
    });
    scan(document);
    const mo = new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.type === "characterData" && m.target.parentElement?.closest(".truncate")) queue(m.target.parentElement.closest(".truncate") as HTMLElement);
        m.addedNodes.forEach((n) => { if (n instanceof HTMLElement) { if (n.classList.contains("truncate")) scan(n.parentElement ?? n); else scan(n); } });
      }
    });
    mo.observe(document.body, { childList: true, subtree: true, characterData: true });
    return () => { mo.disconnect(); ro.disconnect(); cancelAnimationFrame(frame); };
  }, [min]);
}
